import { GitBranch01Icon, PlusIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  OptionalLabel,
} from "@/kit/ui/form";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { useValibotForm } from "@/utils/validation";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import * as v from "valibot";
import { Input } from "./Input";
import { useMutation, graphql, useFragment } from "react-relay";
import { DatasetVersionCreateButtonMutation as DatasetVersionCreateButtonMutationType } from "./__generated__/DatasetVersionCreateButtonMutation.graphql";
import { toast } from "@/utils/toast";
import { logger } from "@/common/logger";
import { relayErrorMessage } from "@/utils/relay";
import { DatasetVersionCreateButtonFragment$key } from "./__generated__/DatasetVersionCreateButtonFragment.graphql";
import { Combobox } from "@/kit/ui/combobox";
import { generatePath, useNavigate } from "react-router";
import { semverSchema } from "@/utils/schema";
import { useState } from "react";
import { inc } from "semver-ts";
import { splitDatasetSlug } from "@/utils/routing";

const DatasetVersionCreateButtonFragment = graphql`
  fragment DatasetVersionCreateButtonFragment on Dataset {
    id
    latestVersion {
      id
      version
    }
    latestCreatedVersion: versions(first: 1) {
      nodes {
        id
        version
      }
    }
    versionsInput: versions(first: 20)
      @connection(key: "DatasetVersionCreateButtonFragment_versionsInput") {
      edges {
        node {
          id
          version
          publishedAt
        }
      }
    }
  }
`;

const DatasetVersionCreateButtonMutation = graphql`
  mutation DatasetVersionCreateButtonMutation(
    $datasetId: ID!
    $input: CreateDatasetVersionInput!
  ) {
    createDatasetVersion(datasetId: $datasetId, input: $input) {
      id
      version
      dataset {
        slug
        ...DatasetVersionCreateButtonFragment
        ...DatasetVersionHistoryFragment
      }
      ...CardVersionHistoryFragment
      ...DatasetUseButtonFragment
      ...DatasetVersionAsideContentFragment
    }
  }
`;

const schema = (intl: IntlShape) =>
  v.object({
    version: semverSchema(intl),
    datasetVersionId: v.nullable(v.pipe(v.string(), v.nonEmpty(), v.trim())),
  });

interface Props {
  dataset: DatasetVersionCreateButtonFragment$key;
}

export function DatasetVersionCreateButton(props: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const {
    versionsInput: versions,
    id,
    latestCreatedVersion,
    latestVersion,
  } = useFragment(DatasetVersionCreateButtonFragment, props.dataset);

  const datasetVersion =
    latestVersion ?? latestCreatedVersion.nodes.map((node) => node)[0];
  const version =
    inc(datasetVersion.version, "minor") ?? datasetVersion.version;

  const form = useValibotForm(schema(intl), {
    mode: "onChange",
    values: {
      version,
      datasetVersionId: datasetVersion.id,
    },
  });
  const [commitMutation, isMutationInFlight] =
    useMutation<DatasetVersionCreateButtonMutationType>(
      DatasetVersionCreateButtonMutation,
    );

  const doSubmit = form.handleSubmit((data) =>
    commitMutation({
      variables: {
        datasetId: id,
        input: {
          version: data.version,
          datasetVersionId: data.datasetVersionId,
        },
      },
      onCompleted({ createDatasetVersion: { dataset, version } }, _) {
        toast.success(
          <FormattedMessage
            defaultMessage="Dataset version {version} created with success"
            values={{ version }}
          />,
        );
        navigate(
          generatePath("/datasets/:owner/:slug/versions/:version", {
            ...splitDatasetSlug(dataset.slug),
            version,
          }),
        );
        setOpen(false);
      },
      onError(error) {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    }),
  );

  return (
    <ResponsiveModal open={open} onOpenChange={setOpen}>
      <ResponsiveModalTrigger asChild>
        <Button size="lg" className="sm:w-auto w-full">
          <PlusIcon className="w-5 h-5" />
          <FormattedMessage defaultMessage="Create new version" />
        </Button>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent className="w-96 bg-linear-to-b from-[#EFEDFC] from-[-16.39%] to-white to-[65.62%] ">
        <Form form={form} onSubmit={doSubmit} className="w-full">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="bg-brand-50 rounded-md p-2 w-fit">
              <GitBranch01Icon className="text-brand-500 w-8 h-8" />
            </div>
            <ResponsiveModalTitle>
              <FormattedMessage defaultMessage="Create dataset version" />
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center w-10/12">
              <FormattedMessage defaultMessage="Make a new version of this dataset. Enter a name,and choose the version to copy from." />
            </ResponsiveModalDescription>

            <FormField
              control={form.control}
              name="version"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    <FormattedMessage defaultMessage="Version name" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({
                        defaultMessage: "Enter a unique SemVer name",
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="datasetVersionId"
              render={({ field }) => {
                return (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage defaultMessage="Based on version" />
                      <OptionalLabel />
                    </FormLabel>
                    <FormControl>
                      <Combobox
                        items={versions.edges.map(({ node: version }) => ({
                          value: version.id,
                          label: version.publishedAt
                            ? intl.formatMessage(
                                { defaultMessage: "{version} (Active)" },
                                { version: version.version },
                              )
                            : version.version,
                        }))}
                        emptyMessage={intl.formatMessage({
                          defaultMessage: "No version found",
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <div className="w-full flex flex-row gap-2 mt-4">
              <ResponsiveModalClose asChild>
                <Button
                  variant="secondary"
                  disabled={isMutationInFlight}
                  className="w-full"
                >
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
              </ResponsiveModalClose>
              <Button
                type="submit"
                disabled={isMutationInFlight}
                className="w-full"
              >
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          </div>
        </Form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
